// @givmo/app-admin — the methodology affordance (spec §135 / §182).
//
// The redesign spec's honesty rule: "Every new number states its basis — a methodology tooltip or a
// caption." This is that control, in one place, so a reader learns one interaction and every figure on
// the surface explains itself the same way.
//
// WHY A POPOVER AND NOT `title=`. The grouped-approval `title` tooltip is the exact hygiene item the
// spec calls out (§180) — a native tooltip is invisible to keyboard and screen-reader users and never
// appears on touch. `<Popover.Trigger>` renders a real button and wires
// `aria-haspopup`/`aria-expanded`/`aria-controls`; the popup is a `role="dialog"` with an accessible
// name. So the basis is reachable by keyboard, by screen reader, and by tap.
//
// It carries NO number of its own — it explains one. Whatever renders the figure this annotates must
// still render an em-dash (never `$0`) when the figure is absent: a methodology note attached to a
// value that is not there would be a second wrong statement, not a mitigation.

import type { ReactNode } from 'react';
import { Icon, Popover, testIdProp } from '@givmo/design-system';
import { Info } from 'lucide-react';

export interface MethodologyHintProps {
  /** What the number counts and over what period. Plain sentences — this is read, not skimmed. */
  children: ReactNode;
  /**
   * What the hint is about, e.g. "Program utilization". Names the trigger ("How Program utilization is
   * calculated") and the popup, so a screen-reader user landing on the button knows which figure it
   * belongs to without having to infer a visual association.
   */
  subject: string;
  testId?: string;
}

export function MethodologyHint({ children, subject, testId }: MethodologyHintProps) {
  const label = `How ${subject} is calculated`;

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={label}
        className="inline-flex size-16 shrink-0 items-center justify-center rounded-sm text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:shadow-ring"
        {...testIdProp(testId)}
      >
        <Icon icon={Info} size={14} aria-hidden />
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="end"
        className="max-w-[280px] p-12"
        aria-label={label}
        testId={testId === undefined ? undefined : `${testId}-popup`}
      >
        <p className="font-sans text-s13 leading-h15 tracking-normal text-text-secondary">
          {children}
        </p>
      </Popover.Content>
    </Popover.Root>
  );
}
